/**
 * ClientContext — Partage les données client entre toutes les pages client
 *
 * Permet à la sidebar et aux pages enfants (RS, Prog, Transits, etc.)
 * d'accéder aux données client sans recharger.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import type { ClientDetail, ChartAnalysis } from '../types/clients.js';

interface ClientContextValue {
  client: ClientDetail | null;
  setClient: React.Dispatch<React.SetStateAction<ClientDetail | null>>;
  isLoading: boolean;
  error: string;
  selectedChartId: number | null;
  setSelectedChartId: (id: number | null) => void;
  analysis: ChartAnalysis | null;
  setAnalysis: (a: ChartAnalysis | null) => void;
  analysisLoading: boolean;
  loadAnalysis: (chartId: number) => void;
  reloadClient: () => Promise<void>;
  handleDeleteChart: (chartId: number) => Promise<void>;
  handleDeleteFile: (fileId: number) => Promise<void>;
  formatDate: (dateStr: string) => string;
}

const ClientContext = createContext<ClientContextValue | null>(null);

export function useClient() {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error('useClient doit être utilisé dans un ClientLayout');
  return ctx;
}

export function ClientProvider({ children }: { children: React.ReactNode }) {
  const { id } = useParams<{ id: string }>();

  const [client, setClient] = useState<ClientDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedChartId, setSelectedChartId] = useState<number | null>(null);
  const [analysis, setAnalysis] = useState<ChartAnalysis | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  /** Charge ou recharge les données du client depuis l'API */
  const fetchClient = useCallback(async (autoSelectChart = true) => {
    if (!id) return;
    setIsLoading(true);
    setError('');
    try {
      const res = await apiFetch<ClientDetail>(`/clients/${id}`);
      if (res.success && res.data) {
        setClient(res.data);
        if (autoSelectChart && res.data.charts.length > 0) {
          const natal = res.data.charts.find((c) => c.type === 'NATAL');
          const chart = natal ?? res.data.charts[0]!;
          setSelectedChartId(chart.id);
          loadAnalysisForChart(chart.id);
        }
      } else {
        setError(res.error ?? 'Impossible de charger le client');
      }
    } catch {
      setError('Erreur de connexion au serveur');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchClient(); }, [fetchClient]);

  /** Recharge les données du client (après import, suppression, etc.) sans changer le chart sélectionné */
  const reloadClient = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiFetch<ClientDetail>(`/clients/${id}`);
      if (res.success && res.data) {
        setClient(res.data);
      }
    } catch { /* silencieux */ }
  }, [id]);

  function loadAnalysisForChart(chartId: number) {
    setAnalysis(null);
    setAnalysisLoading(true);

    apiFetch<ChartAnalysis>(`/clients/${id}/charts/${chartId}/analysis`)
      .then((res) => {
        if (res.success && res.data) {
          setAnalysis(res.data);
        }
      })
      .finally(() => setAnalysisLoading(false));
  }

  function loadAnalysis(chartId: number) {
    if (selectedChartId === chartId) {
      setSelectedChartId(null);
      setAnalysis(null);
      return;
    }
    setSelectedChartId(chartId);
    loadAnalysisForChart(chartId);
  }

  async function handleDeleteFile(fileId: number) {
    if (!confirm('Supprimer ce document ?')) return;
    try {
      const res = await apiFetch(`/clients/${id}/files/${fileId}`, { method: 'DELETE' });
      if (res.success) {
        setClient((prev) => prev ? { ...prev, fichiers: prev.fichiers.filter((f) => f.id !== fileId) } : prev);
      }
    } catch { /* silencieux */ }
  }

  async function handleDeleteChart(chartId: number) {
    if (!confirm('Supprimer ce thème ? Cette action est irréversible.')) return;
    try {
      const res = await apiFetch(`/clients/${id}/charts/${chartId}`, { method: 'DELETE' });
      if (res.success) {
        setClient((prev) => {
          if (!prev) return prev;
          const charts = prev.charts.filter((c) => c.id !== chartId);
          return { ...prev, charts };
        });
        if (selectedChartId === chartId) {
          setSelectedChartId(null);
          setAnalysis(null);
        }
      }
    } catch { /* silencieux */ }
  }

  function formatDate(dateStr: string): string {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  return (
    <ClientContext.Provider value={{
      client, setClient, isLoading, error,
      selectedChartId, setSelectedChartId,
      analysis, setAnalysis, analysisLoading,
      loadAnalysis, reloadClient, handleDeleteChart, handleDeleteFile, formatDate,
    }}>
      {children}
    </ClientContext.Provider>
  );
}
